import React, { useEffect, useState } from "react";
import { Modal } from "antd";
import classNames from "classnames";
import styles from "./modal.module.css";

interface CustomModalProps {
  title: string;
  okText?: string;
  cancelText?: string;
  isOpen: boolean;
  onOk: (...args: any) => void;
  onCancel: () => void;
  children: React.ReactNode;
  isFullScreen?: boolean; // Optional prop to determine if the modal is full-screen
  hideOkButton?: boolean; // Optional prop to hide the OK button
  hideCancelButton?: boolean; // Optional prop to hide the Cancel button
  useCustomHeader?: boolean; // New prop to conditionally apply header styles
  disableOk?: boolean; // New prop to disable the OK button
  width?: string | number;
  style?: React.CSSProperties;
  customColor?: string;
  footer?: React.ReactNode[] | null;
  bodyPadding?: string;
  cusHeader?: boolean;
  closeIcon?: boolean;
  maskClosable?: boolean;
}

/**
 * A reusable modal component that wraps Ant Design's Modal component
 * and expects a title, isOpen flag, onOk callback, onCancel callback, children,
 * and optional flags for full-screen mode, hiding the OK button, and disabling the OK button.
 */
const CustomModal: React.FC<CustomModalProps> = ({
  title,
  isOpen,
  onOk,
  onCancel,
  children,
  style,
  okText = "Ok",
  cancelText = "Close",
  isFullScreen = false,
  hideOkButton = false,
  hideCancelButton = false, // Use this prop to hide the Cancel button
  disableOk = false, // Default to false
  width,
  footer,
  bodyPadding = "12px",
  cusHeader = false,
  closeIcon = true,
  maskClosable = false,
}) => {
  const [hideOK, setHideOk] = useState(false);
  const [hideCancel, setHideCancel] = useState(false);

  useEffect(() => {
    setHideOk(hideOkButton);
    setHideCancel(hideCancelButton); // Set the hideCancel state based on the prop
  }, [hideOkButton, hideCancelButton]);

  const cusHeaderStyles = cusHeader
    ? {
        "--custom-header-bg": "white",
        "--custom-header-title-color": "black",
        "--custom-header-close-color": "black",
      }
    : {
        "--custom-header-close-color": "white",
      };

  return (
    <Modal
      footer={footer ? footer : null}
      title={title}
      closeIcon={closeIcon}
      maskClosable={maskClosable}
      open={isOpen}
      onCancel={onCancel}
      width={isFullScreen ? "80%" : width || undefined} // Set width to full screen if needed
      className={classNames([
        isFullScreen ? styles.fullScreen : styles.noFullScreen,
        styles.antModalBody,
        {
          "custom-header": cusHeader,
        },
      ])}
      styles={{
        body: {
          padding: bodyPadding,
        },
      }}
      style={{
        ...style,
        ...cusHeaderStyles,
      }}
      destroyOnClose
    >
      {children}

      {/* Custom footer */}

      <div
        className={styles.customFooter}
        style={{
          display: footer === null ? "none" : "flex",
          justifyContent: okText?.toLowerCase() === "yes" ? "end" : "center",
        }}
      >
        {!hideOK && (
          <span
            onClick={() => {
              if (!disableOk) onOk(); // Prevent onClick if disableOk is true
            }}
            className={classNames(
              okText?.toLowerCase() === "yes"
                ? styles.footerText
                : styles.buttonFooterText,
              { [styles.disabledOk]: disableOk } // Apply disabled style when disableOk is true
            )}
            style={{
              cursor: disableOk ? "not-allowed" : "pointer", // Change cursor to indicate disabled state
              opacity: disableOk ? 0.5 : 1, // Dim the button if disabled
            }}
          >
            {okText}
          </span>
        )}
        {!hideCancel && (
          <span
            onClick={onCancel}
            className={
              okText?.toLowerCase() === "yes"
                ? styles.footerText
                : styles.buttonFooterText
            }
          >
            {cancelText}
          </span>
        )}
      </div>
    </Modal>
  );
};

export default CustomModal;
